import React, { useEffect, useState } from "react";
import { View } from "react-native";
import tw from "twrnc";

import Callout from "@/components/settings/Callout";
import { SectionTitle } from "@/components/settings/SettingsPage";
import AppActivityIndicator from "@/components/shared/AppActivityIndicator";
import countries from "@/components/countries";
import { t, tc, tn } from "@/localization";
import {
  BarList,
  MetricSwitch,
  StatCard,
  StatRow,
  formatNumber,
  formatWatchTime,
  unknownLabel,
} from "./reportParts";

// Mux's report for the last 100 days (evt.live /api/analytics), as Event
// Analytics showed it before our own: for events dated before the switch
// while Mux runs (logic.js reportSource), and under ours in MUX_REPORT_ENABLED
// builds (config.js), to compare the two (`comparison`).

const MUX_ANALYTICS = "https://evt.live/api/analytics/";

// Mux reports device categories in lowercase English ("desktop", "phone"…).
const DEVICES = {
  desktop: () => tc("device", "desktop"),
  phone: () => tc("device", "phone"),
  tablet: () => tc("device", "tablet"),
  tv: () => tc("device", "tv"),
  console: () => tc("device", "console"),
};

const SECTIONS = [
  { dim: "country", title: () => t("Top Countries"), label: (f) => countries[f] || f || unknownLabel() },
  { dim: "region", title: () => t("Top Regions / States"), label: (f) => f || unknownLabel() },
  { dim: "viewer_device_category", title: () => t("Device Categories"), label: (f) => (DEVICES[f] ? DEVICES[f]() : f || unknownLabel()) },
  { dim: "browser", title: () => t("Top Browsers"), label: (f) => f || unknownLabel() },
  { dim: "operating_system", title: () => t("Operating Systems"), label: (f) => f || unknownLabel() },
];

const MuxReport = ({ event, comparison }) => {
  const [data, setData] = useState(undefined); // undefined: loading, null: failed
  const [metric, setMetric] = useState("views");

  useEffect(() => {
    if (!event?.stream_key) return;
    let current = true;
    fetch(`${MUX_ANALYTICS}${event.stream_key}`)
      .then((r) => r.json())
      .then((d) => current && setData(d && d.overall && d.breakdown ? d : null))
      .catch(() => current && setData(null));
    return () => {
      current = false;
    };
  }, [event?.stream_key]);

  const value = (row) =>
    metric === "views" ? row.views : metric === "watchTime" ? row.total_watch_time : row.unique_viewers;
  const display = (row) =>
    metric === "views"
      ? tn("{count} views", "{count} views", row.views, { count: formatNumber(row.views) })
      : metric === "watchTime"
      ? formatWatchTime((row.total_watch_time || 0) / 3.6e6)
      : tn("{count} viewers", "{count} viewers", row.unique_viewers, { count: formatNumber(row.unique_viewers) });

  return (
    <View style={comparison ? tw`mt-6` : null}>
      {comparison && (
        <SectionTitle
          title={t("Mux, for comparison")}
          description={t("The previous report: the last 100 days, browsers that report to Mux only.")}
        />
      )}
      {data === undefined ? (
        <AppActivityIndicator />
      ) : data === null ? (
        <Callout tone="danger">{t("Sorry, there's been an error loading the analytics data.")}</Callout>
      ) : (
        <View>
          <StatRow wide>
            <StatCard title={t("Total Views")} value={formatNumber(data.overall.data.total_views)} />
            <StatCard title={t("Unique Viewers")} value={formatNumber(data.overall.data.unique_viewers)} />
            <StatCard title={t("Total Watch Time")} value={formatWatchTime((data.overall.data.total_watch_time || 0) / 3.6e6)} />
          </StatRow>
          <MetricSwitch
            value={metric}
            onChange={setMetric}
            options={[
              { value: "views", label: t("Views"), icon: "eye-outline" },
              { value: "uniqueViewers", label: t("Unique Viewers"), icon: "people-outline" },
              { value: "watchTime", label: t("Watch Time"), icon: "time-outline" },
            ]}
          />
          {SECTIONS.map((s) => (
            <BarList
              key={s.dim}
              title={s.title()}
              rows={[...((data.breakdown[s.dim] || {}).data || [])]
                .sort((a, b) => (value(b) || 0) - (value(a) || 0))
                .slice(0, 10)
                .map((row, i) => ({ key: `${row.field}-${i}`, label: s.label(row.field), value: value(row) || 0, display: display(row) }))}
            />
          ))}
        </View>
      )}
    </View>
  );
};

export default MuxReport;
